'use strict';

/**
 * Server mirror of src/utils/managementReviewActions.js (OPP-004, GH #7167).
 *
 * Management-review actions (`state.managementReviews[].actions[]`) are Tier 2
 * domain-backed actions. This module reads both the legacy and the Tier 2
 * shape and derives the verification state, fail-safe: an unknown status reads
 * 'pending', a reasonless cancel reads 'pending', a legacy close is never
 * effective, and 'effective' needs a named, dated verifier plus at least one
 * evidence ref whose artefactId resolves (MGMTREVIEW-B-003).
 *
 * Kept identical to the client copy by
 * src/utils/managementReviewActions.parity.test.js. Change both together.
 * Contract: docs/task-system-redesign.md "DomainAction", docs/state-schema.md
 * "ManagementReviewAction".
 */

const MR_ACTION_STATUSES = ['pending', 'in-progress', 'completed', 'cancelled'];
const VERIFICATION_OUTCOMES = ['effective', 'not-effective', 'not-yet'];

const LEGACY_STATUS = { open: 'pending', closed: 'completed' };

const str = (v) => (typeof v === 'string' ? v : '');
const hasText = (v) => typeof v === 'string' && v.trim().length > 0;

function normaliseMrAction(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const rawStatus = str(raw.status);
  let status = MR_ACTION_STATUSES.includes(rawStatus)
    ? rawStatus
    : LEGACY_STATUS[rawStatus] || 'pending';
  if (status === 'cancelled' && !hasText(raw.cancelReason)) status = 'pending';

  const out = {
    ...raw,
    title: hasText(raw.title) ? raw.title : str(raw.description),
    description: str(raw.description) || str(raw.title),
    status,
    assigneeId: hasText(raw.assigneeId) ? raw.assigneeId : str(raw.owner),
    evidenceRefs: Array.isArray(raw.evidenceRefs) ? raw.evidenceRefs : [],
    verificationLog: Array.isArray(raw.verificationLog) ? raw.verificationLog : [],
  };
  delete out.owner;

  if (rawStatus === 'closed') {
    if (!out.completedAt && raw.closedAt) out.completedAt = raw.closedAt;
    // A read-time marker, distinguishable from the migration's own
    // (which carries migratedAt): only the migration may persist one.
    if (!raw.legacy)
      out.legacy = { derived: true, fromStatus: 'closed', closedNote: str(raw.closedNote) };
  }
  return out;
}

function isNamedDatedEntry(e) {
  return (
    !!e &&
    typeof e === 'object' &&
    VERIFICATION_OUTCOMES.includes(e.outcome) &&
    hasText(e.by) &&
    hasText(e.at)
  );
}

function hasResolvableEvidence(action, resolvableArtefactIds) {
  if (!resolvableArtefactIds || typeof resolvableArtefactIds.has !== 'function') return false;
  return (action.evidenceRefs || []).some(
    (r) => r && hasText(r.artefactId) && resolvableArtefactIds.has(r.artefactId)
  );
}

/**
 * Derived, never stored. Returns null for work that is not completed;
 * otherwise one of 'not-verified-legacy' | 'awaiting-verification' |
 * 'effective' | 'not-effective' | 'not-yet'.
 *
 * `action` must already be normalised. `resolvableArtefactIds` is a Set of
 * artefact ids that exist in the tenant; omitted means nothing resolves.
 */
function deriveVerificationState(action, resolvableArtefactIds) {
  if (!action || action.status !== 'completed') return null;
  // Only a verdict given for the current completion counts: after a reopen
  // and a new completion, an older entry owes a fresh one (OPP004A-02). With
  // no readable completion date, no verdict can be placed after it — none counts.
  const since = Date.parse(action.completedAt);
  const log = (action.verificationLog || []).filter(
    (e) => isNamedDatedEntry(e) && !Number.isNaN(since) && Date.parse(e.at) >= since
  );
  const latest = log.length ? log[log.length - 1] : null;
  if (!latest) return action.legacy ? 'not-verified-legacy' : 'awaiting-verification';
  if (latest.outcome === 'effective') {
    return hasResolvableEvidence(action, resolvableArtefactIds)
      ? 'effective'
      : 'awaiting-verification';
  }
  return latest.outcome;
}

// Anything that is not an ISO date reads as undated, so it counts as due:
// a free-text legacy due date must never drop out of the denominator.
const dateOnly = (v) =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : '';

/**
 * GOV-01 counting (docs/measurement/architecture.md §3.4): MR actions
 * completed, verified effective and linked to an ISMS change ÷ MR actions due
 * in the window. Cancelled actions stay in the denominator; an undated action
 * counts as due (a missing due date never shrinks the denominator); a legacy
 * close is never effective. Without `linkedIsmsChangeActionIds` the numerator
 * is 0 — the isms-change ledger (measurement Phase 4) supplies it.
 *
 * opts: { from, to (YYYY-MM-DD, inclusive), resolvableArtefactIds?: Set,
 *         linkedIsmsChangeActionIds?: Set }
 */
function countForGov01(actions, opts = {}) {
  const { from, to, resolvableArtefactIds, linkedIsmsChangeActionIds } = opts;
  const r = {
    denominator: 0,
    numerator: 0,
    open: 0,
    completed: 0,
    cancelled: 0,
    effective: 0,
    notVerifiedLegacy: 0,
    awaitingVerification: 0,
    undated: 0,
  };
  for (const raw of Array.isArray(actions) ? actions : []) {
    const a = normaliseMrAction(raw);
    if (!a) continue;
    const due = dateOnly(a.dueDate);
    if (due) {
      if ((from && due < from) || (to && due > to)) continue;
    } else {
      r.undated += 1;
    }
    r.denominator += 1;
    if (a.status === 'cancelled') {
      r.cancelled += 1;
      continue;
    }
    if (a.status !== 'completed') {
      r.open += 1;
      continue;
    }
    r.completed += 1;
    const v = deriveVerificationState(a, resolvableArtefactIds);
    if (v === 'effective') {
      r.effective += 1;
      if (linkedIsmsChangeActionIds && linkedIsmsChangeActionIds.has(a.id)) r.numerator += 1;
    } else if (v === 'not-verified-legacy') r.notVerifiedLegacy += 1;
    else if (v === 'awaiting-verification') r.awaitingVerification += 1;
  }
  return r;
}

/**
 * True when a review carries a recorded output — an action, a 9.3.3 output or
 * a snapshot — so it is part of the 9.3 record and is never deleted
 * (MGMTREVIEW-B-002, D-1 A on GH #7167). The state-write guard restores such
 * a review; the client never offers to delete one.
 */
function hasRecordedOutputs(review) {
  if (!review || typeof review !== 'object' || Array.isArray(review)) return false;
  if (Array.isArray(review.actions) && review.actions.length > 0) return true;
  if (review.snapshotData) return true;
  const outputs =
    review.outputs && typeof review.outputs === 'object' && !Array.isArray(review.outputs)
      ? Object.values(review.outputs)
      : [];
  return outputs.some(
    (v) =>
      hasText(v) ||
      (Array.isArray(v) && v.length > 0) ||
      (!!v && typeof v === 'object' && !Array.isArray(v))
  );
}

module.exports = {
  MR_ACTION_STATUSES,
  VERIFICATION_OUTCOMES,
  normaliseMrAction,
  deriveVerificationState,
  countForGov01,
  hasRecordedOutputs,
};
